import { useEffect, useRef } from 'react'
import type { FC } from 'react'
import type { RefObject } from 'react'

import { Spin } from '@/components'
import type { T_Transaction } from '@/types/responses'
import { TransactionCard } from './components'
import { SC_TransactionsListWrapper } from './styled'


interface T_TransactionsList {
  transactionsList: T_Transaction[]
  isPending: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => Promise<unknown>
  scrollRootRef?: RefObject<HTMLDivElement>
  useScrollFallback?: boolean
}

export const TransactionsList: FC<T_TransactionsList> = ({
  transactionsList,
  isPending,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  scrollRootRef,
  useScrollFallback = false,
}) => {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = loadMoreRef.current
    if (!node) return
    if (!hasNextPage || !fetchNextPage) return

    let isDisposed = false
    const observer = new IntersectionObserver((entries) => {
      const [ entry ] = entries
      if (!entry?.isIntersecting) return
      if (isDisposed || isFetchingNextPage) return
      void fetchNextPage()
    }, {
      root: scrollRootRef?.current ?? null,
      rootMargin: '0px 0px 120px 0px',
      threshold: 0.01,
    })

    observer.observe(node)

    return () => {
      isDisposed = true
      observer.disconnect()
    }
  }, [ hasNextPage, isFetchingNextPage, fetchNextPage, scrollRootRef ])

  useEffect(() => {
    if (!useScrollFallback) return
    const root = scrollRootRef?.current
    if (!root || !hasNextPage || !fetchNextPage) return

    let rafId: number | null = null
    let isDisposed = false
    const tryLoadMore = () => {
      if (isDisposed) return
      if (isFetchingNextPage) return
      // Используем Math.ceil для scrollTop, так как на некоторых устройствах он может быть дробным
      const remaining = root.scrollHeight - Math.ceil(root.scrollTop) - root.clientHeight
      if (remaining <= 120) {
        void fetchNextPage()
      }
    }
    const scheduleTryLoadMore = () => {
      if (isDisposed) return
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        if (isDisposed) return
        rafId = null
        tryLoadMore()
      })
    }

    root.addEventListener('scroll', scheduleTryLoadMore, { passive: true })
    root.addEventListener('touchend', scheduleTryLoadMore, { passive: true })
    window.addEventListener('resize', scheduleTryLoadMore, { passive: true })
    window.addEventListener('orientationchange', scheduleTryLoadMore, { passive: true })
    scheduleTryLoadMore()

    return () => {
      isDisposed = true
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
        rafId = null
      }
      root.removeEventListener('scroll', scheduleTryLoadMore)
      root.removeEventListener('touchend', scheduleTryLoadMore)
      window.removeEventListener('resize', scheduleTryLoadMore)
      window.removeEventListener('orientationchange', scheduleTryLoadMore)
    }
  }, [
    useScrollFallback,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollRootRef,
    transactionsList.length,
  ])

  if (isPending) {
    return <Spin size='large' />
  }

  if (!transactionsList.length) {
    return <span>Транзакции отсутствуют</span>
  }

  return (
    <SC_TransactionsListWrapper>
      {transactionsList.map((tr) => (
        <TransactionCard
          key={tr.id}
          transaction={tr}
        />
      ))}

      <div ref={loadMoreRef} />
      {isFetchingNextPage && <Spin size='large' />}
    </SC_TransactionsListWrapper>
  )
}
